import { AppShell, Avatar, Burger, Group, Menu, NavLink, ScrollArea, Stack, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconLogout, IconUserCircle } from '@tabler/icons-react';
import { Link, Navigate, NavLink as RouterNavLink, Outlet, useLocation } from 'react-router';
import { useAuth } from '../auth/AuthProvider';
import { Loading } from '../components/ui';
import { NAV } from './nav';

/**
 * The logo (logos/README.md; the back office is light-only). `bar`: the TS monogram beside the
 * wordmark, for the top bar (700 × 137). `full`: monogram, wordmark and tagline stacked, for the
 * sign-in pages (600 × 512).
 */
export function Wordmark({ variant = 'bar' }: { variant?: 'bar' | 'full' }) {
  const [src, width, height] = variant === 'full' ? ['/logos/the-success-time-full.png', 220, Math.round(220 * 512 / 600)] : ['/logos/the-success-time-horizontal.png', 190, Math.round(190 * 137 / 700)];
  return <img src={src} alt="The Success Time" width={width} height={height} style={{ display: 'block', objectFit: 'contain' }} />;
}

/** Every signed-in page: the sidebar, the top bar and the page. */
export function Shell() {
  const { state, user, can, logout } = useAuth();
  const [opened, { toggle, close }] = useDisclosure();
  const location = useLocation();

  if (state.status === 'loading') return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;

  const groups = NAV.map(group => ({ ...group, items: group.items.filter(item => can(item.perm)) })).filter(group => group.items.length > 0);

  return (
    <AppShell header={{ height: 80 }} navbar={{ width: 244, breakpoint: 'sm', collapsed: { mobile: !opened } }} padding="lg">
      <AppShell.Header withBorder>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Group gap={8}>
                <Wordmark />
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.08em' }} visibleFrom="xs">
                  Back office
                </Text>
              </Group>
            </Link>
          </Group>
          <Menu position="bottom-end" width={220}>
            <Menu.Target>
              <UnstyledButton>
                <Group gap={8}>
                  <Avatar size={30} radius="xl" color="brand">
                    {user.name.slice(0, 1)}
                  </Avatar>
                  <Stack gap={0} visibleFrom="xs">
                    <Text size="sm" fw={600} lh={1.2}>
                      {user.name}
                    </Text>
                    <Text size="xs" c="dimmed" lh={1.2}>
                      {user.role.name}
                    </Text>
                  </Stack>
                  <IconChevronDown size={14} />
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item component={Link} to="/account" leftSection={<IconUserCircle size={16} />}>
                My account
              </Menu.Item>
              <Menu.Item color="red" leftSection={<IconLogout size={16} />} onClick={() => void logout()}>
                Sign out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar bg="var(--sb-ink)" withBorder={false}>
        <ScrollArea px={8} pb="md" type="scroll">
          {groups.map(group => (
            <div key={group.label}>
              <div className="sb-nav-group">{group.label}</div>
              {group.items.map(item => (
                <NavLink
                  key={item.to}
                  component={RouterNavLink}
                  to={item.to}
                  label={item.label}
                  leftSection={<item.icon size={18} stroke={1.6} />}
                  active={location.pathname === item.to || location.pathname.startsWith(item.to + '/')}
                  onClick={close}
                  variant="filled"
                  color="brand"
                  styles={{ root: { borderRadius: 6, color: '#d5d8e5', marginBottom: 1 }, label: { fontSize: 14 } }}
                />
              ))}
            </div>
          ))}
        </ScrollArea>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
